import { styled } from '@mui/material';
import AvatarWithName from 'components/new/AvatarWithName';
import { Column } from 'further-ui/components/Layout';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import TabsPanel from 'further-ui/components/TabsPanel';
import { numberToCurrencyString } from 'further-ui/utils';
import React from 'react';
import { sortAllocations } from '../utils/sortAllocations';

const ContentColumn = styled(Column)(({ theme }) => ({
  padding: theme.spacing(
    0,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
  ),
}));

type TransferLinkedCapitalActionCardProps = {
  capitalAction: {
    originalInvestorBreakdown: Array<{
      investorName: string;
      commitmentId: string;
      investments: number;
      fees: number;
      expenses: number;
      returnOfCapital: number;
      capitalContributionUsed: number;
      advanceSubscriptionUsed: number;
      netCallOrDistribution: number;
    }>;
    newInvestorBreakdown: Array<{
      investorName: string;
      commitmentId: string;
      investments: number;
      fees: number;
      expenses: number;
      returnOfCapital: number;
      capitalContributionUsed: number;
      advanceSubscriptionUsed: number;
      netCallOrDistribution: number;
      totalToCall: number;
    }>;
  };
  baseCurrency: string;
};

const TransferLinkedCapitalActionCard: React.FC<
  TransferLinkedCapitalActionCardProps
> = ({ capitalAction, baseCurrency }) => {
  const sortedOriginalBreakdown = sortAllocations(
    capitalAction.originalInvestorBreakdown,
  );
  const sortedNewBreakdown = sortAllocations(
    capitalAction.newInvestorBreakdown,
  );

  const originalRows = sortedOriginalBreakdown.map((row, idx) => ({
    id: `original-${idx}`,
    ...row,
  }));

  const newRows = sortedNewBreakdown.map((row, idx) => ({
    id: `new-${idx}`,
    ...row,
  }));

  type OriginalRow = (typeof originalRows)[number];
  type NewRow = (typeof newRows)[number];

  const sharedColumns = <T extends OriginalRow>(): Array<TableColumn<T>> => [
    {
      key: 'investorName',
      label: 'Name',
      render: (row) => <AvatarWithName name={row.investorName} />,
    },
    {
      key: 'investments',
      label: 'Call - Investments',
      render: (row) =>
        numberToCurrencyString(row.investments, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'expenses',
      label: 'Call - Expenses',
      render: (row) =>
        numberToCurrencyString(row.expenses, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'fees',
      label: 'Call - Fees',
      render: (row) =>
        numberToCurrencyString(row.fees, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'returnOfCapital',
      label: 'Capital refunds',
      render: (row) =>
        numberToCurrencyString(row.returnOfCapital, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'netCallOrDistribution',
      label: 'Net call/(distribution)',
      render: (row) =>
        numberToCurrencyString(row.netCallOrDistribution, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
      withVerticalBorder: true,
    },
    {
      key: 'capitalContributionUsed',
      label: 'Capital contribution used',
      render: (row) =>
        numberToCurrencyString(row.capitalContributionUsed, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      key: 'advanceSubscriptionUsed',
      label: 'Advance subscription used',
      render: (row) =>
        numberToCurrencyString(row.advanceSubscriptionUsed, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  const newColumns: Array<TableColumn<NewRow>> = [
    ...sharedColumns<NewRow>(),
    {
      key: 'totalToCall',
      label: 'Call underpaid/(overpaid)',
      render: (row) =>
        numberToCurrencyString(row.totalToCall, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  return (
    <TabsPanel
      defaultTab="after"
      tabs={[
        {
          key: 'after',
          label: 'New allocation',
          content: (
            <ContentColumn>
              <Table
                showTopBorder
                showHeaderBorder
                columns={newColumns}
                rows={newRows}
                scrollable={true}
              />
            </ContentColumn>
          ),
        },
        {
          key: 'before',
          label: 'Original allocation',
          content: (
            <ContentColumn>
              <Table
                showTopBorder
                showHeaderBorder
                columns={sharedColumns()}
                rows={originalRows}
                scrollable={true}
              />
            </ContentColumn>
          ),
        },
      ]}
    />
  );
};

export default TransferLinkedCapitalActionCard;
